// third-party
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

// project imports
import axios from 'utils/axios';

// types
import { DefaultRootStateProps } from 'types';
import getCategoriesServiceMock from './mocks/getCategoriesServiceMock';
import getFacetsServiceMock from './mocks/getFacetsServiceMock';
import {
    getFacetsServiceProps,
    createFacetServiceProps,
    uploadMassiveFileProps,
    editAdditionalFieldProps,
    editFacetServiceProps,
    editBrandProps,
    createBrandProps,
    downloadMassiveFileProps,
    aditionalFieldsProps,
    downloadMassiveImagesProps
} from 'types/slices/slices';
import { BrandType } from 'types/components/shared';

const initialState: DefaultRootStateProps['catalogue'] = {
    loading: true,
    error: null,
    brands: [],
    facets: [],
    vendors: [],
    categories: [],
    images: [],
    roles: {
        profile: [],
        approval: []
    }
};

const slice = createSlice({
    name: 'catalog',
    initialState,
    reducers: {
        // HAS ERROR
        hasError(state, action) {
            state.error = action.payload;
        }
    },
    extraReducers(builder) {
        // VENDORS
        builder

            .addCase(getVendors.pending, (state) => {
                state.loading = true;
            })
            .addCase(getVendors.fulfilled, (state, action) => {
                state.loading = false;
                state.vendors = action.payload.response;
            });
        builder
            .addCase(getRoles.pending, (state) => {
                state.loading = true;
            })
            .addCase(getRoles.fulfilled, (state, action) => {
                state.loading = false;
                state.roles = action.payload;
            });
        // FACETS
        builder
            .addCase(getFacets.pending, (state) => {
                state.loading = true;
            })
            .addCase(getFacets.fulfilled, (state, action) => {
                state.loading = false;
                state.facets = action.payload;
            });
        builder
            .addCase(getBrands.pending, (state) => {
                state.loading = true;
            })
            .addCase(getBrands.fulfilled, (state, action) => {
                state.loading = false;
                state.brands = action.payload.response;
            });
        // BRANDS
        builder
            .addCase(createBrand.pending, (state) => {
                state.loading = true;
            })
            .addCase(createBrand.fulfilled, (state, action) => {
                const response = action.payload.response as { brandData: BrandType; idMerchant: number }[];

                let newBrans = [...state.brands];

                state.brands = newBrans.map((item) => {
                    const itemResp = response.find((itemResp) => itemResp.idMerchant === item.merchantId);

                    if (itemResp) {
                        return { ...item, brands: [...item.brands, itemResp.brandData] };
                    }

                    return item;
                });

                state.loading = false;
            });

        // CATEGORIES
        builder
            .addCase(getCategories.pending, (state) => {
                state.loading = true;
            })
            .addCase(getCategories.fulfilled, (state, action) => {
                const categories = action.payload.response;
                state.loading = false;
                if (categories) {
                    state.categories = categories;
                }
            });
    }
});

export default slice.reducer;

/* ============ BRANDS ============ */

export const getBrands = createAsyncThunk(`${slice.name}/getBrands`, async ({ idMerchant }: { idMerchant?: number | string }) => {
    const bodyMerchant = {};
    const response = await axios.post(`/loolu/api/brand/search-multicatalog`, bodyMerchant, {
        params: {
            idMerchant // Quitar este parametro en back ya que no importa, trae todos los merchants
        }
    });
    return response.data;
});

export const editBrand = createAsyncThunk(`${slice.name}/editBrand`, async (params: editBrandProps) => {
    const { dataBrand, idMerchant } = params;
    const response = await axios.post(`/loolu/api/brand/create`, dataBrand, {
        params: {
            idMerchant
        }
    });
    return response.data;
});

export const createBrand = createAsyncThunk(`${slice.name}/createBrand`, async (dataBrand: createBrandProps | createBrandProps[]) => {
    const bodyData = Array.isArray(dataBrand) ? dataBrand : [dataBrand];
    let idMerchant;
    if (Array.isArray(dataBrand)) {
        idMerchant = dataBrand[0]?.idMerchant;
    } else {
        idMerchant = dataBrand?.idMerchant;
    }
    const response = await axios.post(`/loolu/api/brand/save-multicatalog`, bodyData, {
        params: {
            idMerchant
        }
    });
    return response.data;
});

export const createBrandFromDrawer = createAsyncThunk(
    `${slice.name}/createBrandFromDrawer`,
    async (dataBrand: createBrandProps | createBrandProps[]) => {
        const bodyData = Array.isArray(dataBrand) ? dataBrand : [dataBrand];
        let idMerchant;
        if (Array.isArray(dataBrand)) {
            idMerchant = dataBrand[0]?.idMerchant;
        } else {
            idMerchant = dataBrand?.idMerchant;
        }
        const response = await axios.post(`/loolu/api/brand/save-multicatalog`, bodyData, {
            params: {
                idMerchant
            }
        });
        return response.data;
    }
);

/* ============ VENDORS ============ */

export const getVendors = createAsyncThunk(
    `${slice.name}/getVendors`,
    async ({ idMerchant, idProvider }: { idMerchant?: number | string; idProvider?: number | string }) => {
        const response = await axios.get(`/loolu/api/multicatalog/supplier/search`, {
            params: {
                idMerchant,
                idProvider
            }
        });
        return response.data;
    }
);

export const getRoles = createAsyncThunk(`${slice.name}/getRoles`, async () => {
    const response = await axios.get(`/loolu/api/catalog/pub/profiles`);
    return response.data;
});

/* ============ FACETS ============ */

export const getFacets = createAsyncThunk(`${slice.name}/getFacets`, async ({ term, page, idMerchant }: getFacetsServiceProps) => {
    let result: any;
    let facetsPag: any;
    if (idMerchant) {
        try {
            const response = await axios.get(`/loolu/api/facets/raw-multicatalog/merchant/${idMerchant}/search`, {
                params: {
                    searchParam: term,
                    pageNum: page
                }
            });
            result = response.data;
        } catch (error) {
            result = getFacetsServiceMock;
        }
        facetsPag = result?.response?.map((item: any) => ({ ...item, totalPages: result?.totalPages }));
    }
    return facetsPag;
});

export const createFacet = createAsyncThunk(`${slice.name}/createFacet`, async ({ idMerchant, data }: createFacetServiceProps) => {
    try {
        const response = await axios.post(`/loolu/api/facets/raw-multicatalog/merchant/${idMerchant}`, [data], {});
        return response.data;
    } catch (e: any) {
        return e.response;
    }
});

export const editFacet = createAsyncThunk(`${slice.name}/editFacet`, async ({ idMerchant, data }: editFacetServiceProps) => {
    try {
        const response = await axios.put(`/loolu/api/facets/raw/merchant/${idMerchant}`, [data], {});
        return response.data;
    } catch (e: any) {
        return e.response;
    }
});

/* ============ CATEGORIES ============ */

export const getCategories = createAsyncThunk(`${slice.name}/getMerchantCategories`, async (idMerchant: number | string) => {
    let result;
    try {
        const response = await axios.get(`/loolu/api/category/search-multicatalog`, {
            params: {
                idMerchant // TODO: QUITAR este parametro
            }
        });
        result = response.data;
    } catch (error) {
        result = getCategoriesServiceMock;
    }
    return result;
});

// MASSIVE LOAD

export const uploadMassiveFile = createAsyncThunk(
    `${slice.name}/uploadMassiveFile`,
    async ({ idMerchant, file, user }: uploadMassiveFileProps) => {
        try {
            const data = new FormData();
            data.append('file', file);

            const response = await axios.post(`/loolu/api/bulkload/merchant/${idMerchant}/user/${user}/upload/file`, data, {
                /*baseURL: STYRK_API_BULKLOAD*/
            });
            return response.data;
        } catch (e: any) {
            return e.response;
        }
    }
);

export const downloadMassiveImages = createAsyncThunk(
    `${slice.name}/images`,
    async ({ idMerchant, idCategory, idBrand, vendorId }: downloadMassiveImagesProps) => {
        try {
            const response = await axios.get(
                `/loolu/api/catalog/images?merchantId=${idMerchant}&categoryId=${idCategory}&brandId=${idBrand}`
            );
            return response.data;
        } catch (e: any) {
            return e.response;
        }
    }
);

export const downloadMassiveFile = createAsyncThunk(
    `${slice.name}/uploadMassiveFile`,
    async ({ idMerchant, user, token, productName, idSKU, idCategory, idBrand, eans }: downloadMassiveFileProps) => {
        try {
            const response = await axios.post(
                `/loolu/api/bulkload/merchant/${idMerchant}/user/${user}/contentRequest`,
                {
                    bearerToken: token,
                    idMerchant,
                    user,
                    productName,
                    idSKU,
                    idCategory,
                    idBrand,
                    eans
                },
                {
                    /*baseURL: STYRK_API_BULKLOAD*/
                }
            );
            return response.data;
        } catch (e: any) {
            return e.response;
        }
    }
);
export const getAllAdditionalFields = createAsyncThunk(
    `${slice.name}/getAdditionalFields`,
    async ({ idMerchant }: { idMerchant: number }) => {
        try {
            const response = await axios.get(`/loolu/api/additionalFields/list`, {
                params: {
                    merchantId: idMerchant,
                    idMerchant
                }
            });
            return response.data;
        } catch (e: any) {
            return e.response;
        }
    }
);

export const getAdditionalFields = createAsyncThunk(
    `${slice.name}/getAdditionalFields`,
    async ({ idMerchant, productId }: { idMerchant: number; productId: number | string }) => {
        try {
            const response = await axios.get(`/loolu/api/product/detail/productSkus`, {
                params: {
                    merchantId: idMerchant,
                    idProd: productId
                }
            });
            return response.data;
        } catch (e: any) {
            return e.response;
        }
    }
);

export const getImage = createAsyncThunk(`${slice.name}/getImage`, async (url: string) => {
    try {
        const response = await axios.get('/loolu/api/catalog/images/getImage', {
            params: { imageUrl: url },
            responseType: 'blob'
        });

        let filename = (url.split('/').pop() || 'default_name.jpg').split('?')[0];

        if (filename.length > 85) {
            filename = filename.substring(filename.length - 85);
        }
        const imageFile = new File([response.data], filename === 'null' ? 'default.jpg' : filename, { type: response.data.type });
        return imageFile;
    } catch (e: any) {
        return e.response;
    }
});

export const createAdditionalField = createAsyncThunk(
    `${slice.name}/createAdditionalField`,
    async ({ additionalFieldData }: { additionalFieldData: aditionalFieldsProps }) => {
        try {
            const response = await axios.post(`/loolu/api/additionalFields/product`, additionalFieldData, {});
            return response.data;
        } catch (e: any) {
            return e.response;
        }
    }
);

export const createMultipleAdditionalFields = createAsyncThunk(
    `${slice.name}/createMultipleAdditionalFields`,
    async ({ additionalFields }: { additionalFields: aditionalFieldsProps[] }) => {
        try {
            const requests = additionalFields.map((data) => axios.post(`/loolu/api/additionalFields/product`, data, {}));

            const responses = await Promise.all(requests);

            const responseData = responses.map((response) => response.data);

            return responseData;
        } catch (e: any) {
            return e.response;
        }
    }
);

export const editAdditionalField = createAsyncThunk(`${slice.name}/createAdditionalField`, async ({ data }: editAdditionalFieldProps) => {
    try {
        const response = await axios.put(`/loolu/api/additionalFields/product`, data, {});
        return response.data;
    } catch (e: any) {
        return e.response;
    }
});

export const deleteAdditionalField = createAsyncThunk(`${slice.name}/deleteAdditionalField`, async (fieldId: number) => {
    try {
        const response = await axios.delete(`/loolu/api/additionalFields/product/${fieldId}`, {});
        return response.data;
    } catch (e: any) {
        return e.response;
    }
});
